
import { BrowserModule } from '@angular/platform-browser';

import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { initializeKeycloak } from './init/keycloak-init.factory';
import { RouteReuseStrategy } from '@angular/router';
import { ParamAwareRouteReuseStrategy } from './core/routing/param-aware-route-reuse-strategy';

import { CurrencyPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { SIDEBAR_ENTITY_HANDLERS } from './core/services/sidebar/sidebar-deep-link.service';
import { GrowthBookService, initializeGrowthBook } from './core/services/growthbook.service';
import { CoreModule } from './core/core.module';
import { ADDITIONAL_FLOWS, Flow } from './core/services/command/command.service';
import { IssueCreateCommandCustomFlow } from './core/services/issue/customFlows/issueCreateCommand.custom-flow';
import { IssueUpdateCommandCustomFlow } from './core/services/issue/customFlows/issueEditCommand.custom-flow';
import { IssueAddTaskCommandCustomFlow } from './core/services/issue/customFlows/issueAddTaskCommand.custom-flow';
import { LabelCreateCommandCustomFlow } from './core/services/label/customFlows/labelCreateCommand.custom-flow';
import { LabelUpdateCommandCustomFlow } from './core/services/label/customFlows/labelUpdateCommand.custom-flow';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApolloModule } from 'apollo-angular';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { environment } from 'src/environments/environment';
import { GraphQLModule } from './graphql.module';
import { AuthInterceptorService } from './guard/auth.service';
import { RentModule } from './rent-module/rent.module';
import { TaskViewSidebarWrapperComponent } from './rent-module/containers/task-view/task-view-sidebar-wrapper.component';
import { IssueViewV2SidebarWrapperComponent } from './core/components/issue-view/issue-view-sidebar-wrapper.component';
import { createTranslateLoader, initializeTranslate } from './shared-module/shared/services/translation.service';
import { TourPlanningModule } from './tour-planning/tour-planning.module';
import moment from 'moment';

@NgModule({ declarations: [
        AppComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        ApolloModule,
        KeycloakAngularModule,
        GraphQLModule,
        BrowserAnimationsModule,
        CoreModule.forRoot(environment, [
            {
                provide: ADDITIONAL_FLOWS,
                multi: true,
                useFactory: (
                    issueCreateCommandCustomFlow: IssueCreateCommandCustomFlow,
                    issueUpdateCommandCustomFlow: IssueUpdateCommandCustomFlow,
                    issueAddTaskCommandCustomFlow: IssueAddTaskCommandCustomFlow
                ) => {
                    const additionalFlows = new Map<string, Flow>();
                    additionalFlows.set('issueCreateCommandCustomFlow', issueCreateCommandCustomFlow);
                    additionalFlows.set('issueUpdateCommandCustomFlow', issueUpdateCommandCustomFlow);
                    additionalFlows.set('issueAddTaskCommandCustomFlow', issueAddTaskCommandCustomFlow);
                    return additionalFlows;
                },
                deps: [
                    IssueCreateCommandCustomFlow,
                    IssueUpdateCommandCustomFlow,
                    IssueAddTaskCommandCustomFlow
                ]
            },
            {
                provide: ADDITIONAL_FLOWS,
                multi: true,
                useFactory: (
                    labelCreateCommandCustomFlow: LabelCreateCommandCustomFlow,
                    labelUpdateCommandCustomFlow: LabelUpdateCommandCustomFlow
                ) => {
                    const additionalFlows = new Map<string, Flow>();
                    additionalFlows.set('labelCreateCommandCustomFlow', labelCreateCommandCustomFlow);
                    additionalFlows.set('labelUpdateCommandCustomFlow', labelUpdateCommandCustomFlow);
                    return additionalFlows;
                },
                deps: [
                    LabelCreateCommandCustomFlow,
                    LabelUpdateCommandCustomFlow
                ]
            }
        ]),
        RentModule,
        TourPlanningModule,
        ApmModule,
        ToastModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader
            }
        })], providers: [
        AuthInterceptorService,
        MessageService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
            deps: [KeycloakService, GrowthBookService, TranslateService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptorService,
            multi: true,
        },
        CurrencyPipe,
        ApmService,
        PrimeNGConfig,
        {
            // Ohne das bleibt eine Detailansicht beim Wechsel auf einen Geschwister-Datensatz
            // (z. B. /issues/issue/A -> /issues/issue/B) auf dem alten Stand stehen.
            provide: RouteReuseStrategy,
            useClass: ParamAwareRouteReuseStrategy
        },
        {
            provide: ErrorHandler,
            useClass: ApmErrorHandler
        },
        GrowthBookService,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeGrowthBook,
            deps: [GrowthBookService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeTranslate,
            deps: [TranslateService],
            multi: true
        },
        {
            provide: SIDEBAR_ENTITY_HANDLERS,
            useValue: {
                entityType: 'task',
                component: TaskViewSidebarWrapperComponent,
                inputKey: 'taskId'
            },
            multi: true
        },
        {
            provide: SIDEBAR_ENTITY_HANDLERS,
            useValue: {
                entityType: 'issue',
                component: IssueViewV2SidebarWrapperComponent,
                inputKey: 'issueId'
            },
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {

  constructor(private primeNGConfig: PrimeNGConfig, service: ApmService) {
    moment.locale('de');
    const apm = service.init({
      active: environment.apmActive,
      serviceName: environment.name,
      serverUrl: environment.apmServerUrl,
      distributedTracingOrigins: [environment.backendUri],
      environment: environment.env,
      breakdownMetrics: true,
      serviceVersion: environment.version
    })
    // Set the locale for PrimeNG components
    this.primeNGConfig.setTranslation({
      monthNames: [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
        'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
      ],
      dayNames: [
        'Sonntag', 'Montag', 'Dienstag', 'Mittwoch',
        'Donnerstag', 'Freitag', 'Samstag'
      ],
      dayNamesMin: [
        'So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'
      ],
      firstDayOfWeek: 1
      // ... Add more translations as needed
    });

  }
}
